import type {
  BatchProcessItemDto,
  FsmCompletedWorkType,
  FsmSyncCategoryTotalMetadataDto,
  FsmSyncCostlessChargeMetadataDto,
  FsmSyncTechMetadataDto,
  FsmSyncUncategorizedChargeMetadataDto,
} from "@fp/admin-api-client";
import {
  BatchProcessItemOperation,
  BatchProcessItemStatus,
  FsmSyncItemMetadataDtoKind,
} from "@fp/admin-api-client";
import { formatCurrency } from "@/lib/format-currency";
import { fsmCompletedWorkTypeLabels } from "@/lib/fsm-platform";

const OUTCOME_COPY: Record<string, string> = {
  AwaitingTechMapping:
    "Every tech must be linked to a talent member or an organization on your roster before this job can pay out.",
};

function categoryTotalLabel(name: string) {
  return Object.hasOwn(fsmCompletedWorkTypeLabels, name)
    ? fsmCompletedWorkTypeLabels[name as FsmCompletedWorkType]
    : name;
}

function isResolved(tech: FsmSyncTechMetadataDto) {
  return tech.resolvedPayee != null;
}

function TechName({
  email,
  isMember,
}: {
  email: string | null | undefined;
  isMember: boolean;
}) {
  return (
    <>
      {email ?? "(no email)"}
      {!isMember && <span className="italic"> — not on the roster</span>}
    </>
  );
}

function PayoutBasis({
  categoryTotals,
  uncategorizedCharges,
  costlessCharges,
}: {
  categoryTotals: FsmSyncCategoryTotalMetadataDto[];
  uncategorizedCharges: FsmSyncUncategorizedChargeMetadataDto[];
  costlessCharges: FsmSyncCostlessChargeMetadataDto[];
}) {
  return (
    <>
      {categoryTotals.length > 0 && (
        <div>
          Cost:{" "}
          {categoryTotals.map((categoryTotal, index) => (
            <span key={categoryTotal.name}>
              {index > 0 && " · "}
              {categoryTotalLabel(categoryTotal.name)} ${formatCurrency(categoryTotal.costAmount)}
              {!categoryTotal.isPayable && (
                <span className="italic"> — not payable</span>
              )}
            </span>
          ))}
        </div>
      )}

      {uncategorizedCharges.map((charge) => (
        <div key={charge.name}>
          {charge.name} ${formatCurrency(charge.costAmount)}
          <span className="italic"> — not in your price book</span>
        </div>
      ))}

      {costlessCharges.map((charge) => (
        <div key={charge.name}>
          {charge.name} billed ${formatCurrency(charge.billedAmount)}
          <span className="italic"> — no cost assigned</span>
        </div>
      ))}
    </>
  );
}

export function FsmExecuteSyncItemDetail({
  item,
}: {
  item: BatchProcessItemDto;
}) {
  const explanation = item.outcomeType
    ? OUTCOME_COPY[item.outcomeType]
    : undefined;

  const metadata =
    item.metadata?.kind === FsmSyncItemMetadataDtoKind.fsmSync
      ? item.metadata
      : null;
  const payoutPoolAmount = metadata?.payoutPoolAmount;
  const techs = metadata?.techs ?? [];
  const categoryTotals = metadata?.categoryTotals ?? [];
  const uncategorizedCharges = metadata?.uncategorizedCharges ?? [];
  const costlessCharges = metadata?.costlessCharges ?? [];

  const hasCharges =
    categoryTotals.length > 0 ||
    uncategorizedCharges.length > 0 ||
    costlessCharges.length > 0;

  if (!explanation && techs.length === 0 && !hasCharges) {
    return null;
  }

  const isWhatIf = item.operation === BatchProcessItemOperation.Preview;
  const isPayable = item.status === BatchProcessItemStatus.Succeeded;
  const amount =
    payoutPoolAmount != null ? `$${formatCurrency(payoutPoolAmount)}` : null;
  const [onlyTech] = techs;

  return (
    <div className="text-xs text-tertiary">
      {explanation && <div>{explanation}</div>}

      {techs.length === 1 && (
        <div>
          {isPayable ? (
            <>
              {isWhatIf ? "Would create" : "Created"} a payout request
              {amount ? ` for ${amount}` : ""} to{" "}
              <TechName
                email={onlyTech.email}
                isMember={isResolved(onlyTech)}
              />
              .
            </>
          ) : (
            <>
              {amount ? `${amount} for ` : ""}
              <TechName
                email={onlyTech.email}
                isMember={isResolved(onlyTech)}
              />
            </>
          )}
        </div>
      )}

      {techs.length > 1 && (
        <div>
          <div>
            {isPayable
              ? `${isWhatIf ? "Would create" : "Created"} ${techs.length} payout requests${amount ? `, splitting ${amount}` : ""} between:`
              : `${amount ? `${amount} ` : ""}to be split across ${techs.length} techs:`}
          </div>
          <ul className="mt-0.5 flex flex-col gap-0.5 pl-3">
            {techs.map((tech, index) => (
              <li key={tech.email ?? index}>
                <TechName
                  email={tech.email}
                  isMember={isResolved(tech)}
                />
              </li>
            ))}
          </ul>
        </div>
      )}

      <PayoutBasis
        categoryTotals={categoryTotals}
        uncategorizedCharges={uncategorizedCharges}
        costlessCharges={costlessCharges}
      />
    </div>
  );
}